import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocalStorageManager } from '@examdojo/browser/local-storage';
import { ButtonComponent } from '@examdojo/core/button';
import { environment } from '@examdojo/core/environment';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { TextInputComponent } from '@examdojo/core/input';
import { ModalController } from '@ionic/angular/standalone';
import { EMPTY, finalize, tap } from 'rxjs';

interface DatasetHistoryStorage {
  recentDatasets: string[];
}

const MAX_RECENT_DATASETS = 3;

@Component({
  selector: 'dojo-dataset-upload-dialog',
  imports: [ReactiveFormsModule, ButtonComponent, TextInputComponent],
  templateUrl: './dataset-upload-dialog.component.html',
  styleUrl: './dataset-upload-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col gap-4 p-6',
  },
})
export class DatasetUploadDialogComponent implements OnInit {
  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly modalCtrl: ModalController,
  ) {}

  /** The attempt ID to upload dataset row for - passed from the dialog service */
  readonly attemptId = input<string>();

  private readonly storageManager = new LocalStorageManager<DatasetHistoryStorage>('dataset-upload-history');

  readonly datasetNameCtrl = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1)],
    nonNullable: true,
  });

  readonly datasetPresets = signal<string[]>([]);

  readonly loading = signal<boolean>(false);

  ngOnInit() {
    const history = this.storageManager.getValue();
    const recentDatasets = history?.recentDatasets ?? [];

    if (recentDatasets.length > 0) {
      this.datasetNameCtrl.setValue(recentDatasets[0]);
      this.datasetPresets.set(recentDatasets.slice(1, MAX_RECENT_DATASETS));
    }
  }

  closeDialog() {
    this.modalCtrl.dismiss();
  }

  uploadDatasetRow() {
    const datasetName = this.datasetNameCtrl.value?.trim();
    const attemptId = this.attemptId();

    if (!datasetName || !attemptId) {
      return;
    }

    this.loading.set(true);

    const url = `${
      environment.examdojo.examdojoApiUrl
    }/review/question-attempts/${attemptId}/datasets/${encodeURIComponent(datasetName)}/rows`;

    this.http
      .post<void>(url, {})
      .pipe(
        this.errorHandlerService.catchError(`Failed to upload dataset row for attempt ${attemptId}`, () => EMPTY, {
          toast: `Failed to upload the dataset row`,
        }),
        tap(() => {
          this.saveToHistory(datasetName);
          this.modalCtrl.dismiss();
        }),
        finalize(() => {
          this.loading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveToHistory(datasetName: string) {
    const history = this.storageManager.getValue();
    const recentDatasets = history?.recentDatasets ?? [];

    const filtered = recentDatasets.filter((name) => name !== datasetName);
    const updated = [datasetName, ...filtered].slice(0, MAX_RECENT_DATASETS);

    this.storageManager.setValue({ recentDatasets: updated });
  }
}
